import { Activity, ChevronRight, Wrench } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { exportarCsv } from '@/lib/export';
import {
  cn,
  formatCurrency,
  formatDateValue,
  formatDateTimeValue,
  formatHoraValue,
  formatNumber,
  toNumber,
} from '@/lib/utils';

import { CalibracionNav } from '../components/calibracion-nav';
import { ReporteCalibracionFiltros } from '../components/reporte-filtros';
import { useCalibracionFiltros, useReporteCalibracion } from '../hooks/use-calibracion';
import { duracionTexto, ejecutorTexto, rangoMesActual } from '../lib/calibracion-format';
import type { Calibracion, DetalleReporteFiltros } from '../calibracion.types';

const COLUMNAS_REPORTE =
  'grid grid-cols-[2.5rem_7rem_7rem_5.5rem_minmax(0,1fr)_4rem_6rem_7rem_5rem_6.5rem_1.5rem] items-center gap-x-3';

/** Indicador del resumen del período. */
function Indicador({
  label,
  valor,
  detalle,
}: {
  label: string;
  valor: string;
  detalle?: string;
}) {
  return (
    <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{label}</p>
      <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">{valor}</p>
      {detalle ? <p className="mt-0.5 text-xs text-ink-500">{detalle}</p> : null}
    </div>
  );
}

/**
 * Reporte de calibración (`/monitoreo/calibracion/reporte`).
 *
 * Puerto de `Calibration_report::index` + `get_data`: cabecera por calibración
 * con sus puntos, el volumen despachado y las transacciones asociadas.
 */
export function CalibracionReportePage() {
  useDocumentTitle('Calibración · Reporte');

  const [filtros, setFiltros] = useState<DetalleReporteFiltros>(() => ({
    ...rangoMesActual(),
    executor: 'all',
  }));
  const [abierta, setAbierta] = useState<number | null>(null);

  const catalogo = useCalibracionFiltros();
  const reporte = useReporteCalibracion({
    fecha_inicio: filtros.fecha_inicio,
    fecha_fin: filtros.fecha_fin,
    executor: filtros.executor,
  });

  const calibraciones = reporte.data?.calibrations ?? [];
  const resumen = reporte.data?.resumen;

  const exportar = () => {
    exportarCsv(
      'reporte-calibracion',
      [
        'N°',
        'Inicio',
        'Fin',
        'Duración (min)',
        'Ejecutor',
        'Puntos',
        'Lados',
        'Productos',
        'Volumen',
        'Importe',
        'Transacciones',
        'Estado',
      ],
      calibraciones.map((calibracion) => [
        calibracion.calibration_id,
        formatDateTimeValue(calibracion.calibration_started_at),
        formatDateTimeValue(calibracion.calibration_completed_at),
        calibracion.duracion_minutos ?? 0,
        ejecutorTexto(calibracion.executor_name),
        calibracion.summary.total_puntos,
        calibracion.summary.total_bombas,
        calibracion.summary.total_productos,
        calibracion.volume_data.total_volume.toFixed(3),
        calibracion.volume_data.total_amount.toFixed(2),
        calibracion.volume_data.total_transactions,
        calibracion.calibration_status === 'finished' ? 'Finalizada' : 'En proceso',
      ]),
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Activity className="size-6 text-brand-500" aria-hidden="true" />
            Reporte de calibración
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Calibraciones ejecutadas en el período, con el volumen despachado en cada una.
          </p>
        </div>

        <CalibracionNav active="reporte" />
      </header>

      <SectionCard
        title="Filtros"
        description="El período se filtra por la fecha de inicio de cada calibración"
      >
        <ReporteCalibracionFiltros
          filtros={filtros}
          onFiltrosChange={setFiltros}
          catalogo={catalogo.data}
          onExportar={exportar}
          exportandoDeshabilitado={calibraciones.length === 0}
          onConsultar={() => void reporte.refetch()}
          consultando={reporte.isRefetching}
        />
      </SectionCard>

      {reporte.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica el rango de fechas e inténtalo nuevamente.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void reporte.refetch()}
              loading={reporte.isRefetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {reporte.isPending ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-16 rounded-card" />
          ))}
        </div>
      ) : resumen ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Indicador
            label="Calibraciones"
            valor={formatNumber(resumen.total_calibraciones)}
            detalle={`${resumen.calibraciones_completadas} finalizadas · ${resumen.calibraciones_pendientes} en proceso`}
          />
          <Indicador
            label="Duración promedio"
            valor={duracionTexto(resumen.promedio_duracion)}
            detalle={`${resumen.total_ejecutores} ${resumen.total_ejecutores === 1 ? 'ejecutor' : 'ejecutores'}`}
          />
          <Indicador
            label="Lados calibrados"
            valor={formatNumber(resumen.bombas_calibradas)}
            detalle={`${resumen.productos_calibrados} ${resumen.productos_calibrados === 1 ? 'producto' : 'productos'}`}
          />
          <Indicador
            label="Volumen total"
            valor={`${formatNumber(resumen.volume_stats.total_volume)} gal`}
            detalle={`Promedio ${formatNumber(resumen.volume_stats.promedio_volume)} gal`}
          />
          <Indicador
            label="Importe total"
            valor={formatCurrency(resumen.volume_stats.total_amount)}
            detalle={`${formatNumber(resumen.volume_stats.total_transactions)} transacciones`}
          />
          <Indicador
            label="Primera transacción"
            valor={formatDateValue(resumen.volume_stats.primera_transaccion)}
            detalle={
              resumen.volume_stats.primera_transaccion
                ? `a las ${formatHoraValue(resumen.volume_stats.primera_transaccion)}`
                : 'Sin transacciones vinculadas'
            }
          />
          <Indicador
            label="Última transacción"
            valor={formatDateValue(resumen.volume_stats.ultima_transaccion)}
            detalle={
              resumen.volume_stats.ultima_transaccion
                ? `a las ${formatHoraValue(resumen.volume_stats.ultima_transaccion)}`
                : 'Sin transacciones vinculadas'
            }
          />
          <Indicador
            label="Período"
            valor={`${formatDateValue(filtros.fecha_inicio)}`}
            detalle={`al ${formatDateValue(filtros.fecha_fin)}`}
          />
        </div>
      ) : null}

      <SectionCard
        title="Calibraciones del período"
        description="Pulsa una fila para ver sus puntos, lados y transacciones"
        action={
          <Badge variant="outline">
            {calibraciones.length} {calibraciones.length === 1 ? 'registro' : 'registros'}
          </Badge>
        }
      >
        {reporte.isPending ? (
          <Skeleton className="h-56 rounded-card" />
        ) : calibraciones.length === 0 ? (
          <div className="rounded-control border border-dashed border-ink-200 px-4 py-12 text-center">
            <Wrench className="mx-auto size-5 text-ink-300" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-ink-700">
              Sin calibraciones en el período
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              Prueba con otro rango de fechas o con los atajos de período.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[68rem]">
              <div
                className={cn(
                  COLUMNAS_REPORTE,
                  'border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400',
                )}
              >
                <span>N°</span>
                <span>Inicio</span>
                <span>Fin</span>
                <span>Duración</span>
                <span>Ejecutor</span>
                <span className="text-right">Puntos</span>
                <span className="text-right">Volumen</span>
                <span className="text-right">Importe</span>
                <span className="text-right">Ops</span>
                <span>Estado</span>
                <span />
              </div>

              <ul className="mt-1">
                {calibraciones.map((calibracion) => (
                  <FilaCalibracion
                    key={calibracion.calibration_id}
                    calibracion={calibracion}
                    abierta={abierta === calibracion.calibration_id}
                    onToggle={() =>
                      setAbierta(
                        abierta === calibracion.calibration_id ? null : calibracion.calibration_id,
                      )
                    }
                  />
                ))}
              </ul>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/** Fila de una calibración, expandible para ver su detalle. */
function FilaCalibracion({
  calibracion,
  abierta,
  onToggle,
}: {
  calibracion: Calibracion;
  abierta: boolean;
  onToggle: () => void;
}) {
  const finalizada = calibracion.calibration_status === 'finished';

  return (
    <li className="border-b border-ink-100 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={abierta}
        className={cn(
          COLUMNAS_REPORTE,
          'w-full py-2 text-left transition-colors hover:bg-ink-50/70',
        )}
      >
        <span className="font-mono text-xs font-semibold text-ink-700 tabular-nums">
          #{calibracion.calibration_id}
        </span>
        <span className="text-xs text-ink-600">
          {formatDateTimeValue(calibracion.calibration_started_at)}
        </span>
        <span className="text-xs text-ink-600">
          {formatDateTimeValue(calibracion.calibration_completed_at)}
        </span>
        <span className="font-mono text-xs text-ink-600 tabular-nums">
          {duracionTexto(calibracion.duracion_minutos)}
        </span>
        <span className="truncate text-xs text-ink-700">
          {ejecutorTexto(calibracion.executor_name)}
        </span>
        <span className="text-right font-mono text-xs text-ink-600 tabular-nums">
          {calibracion.summary.total_puntos}
        </span>
        <span className="text-right font-mono text-xs text-ink-800 tabular-nums">
          {formatNumber(calibracion.volume_data.total_volume)}
        </span>
        <span className="text-right font-mono text-xs font-semibold text-ink-900 tabular-nums">
          {formatCurrency(calibracion.volume_data.total_amount)}
        </span>
        <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
          {calibracion.volume_data.total_transactions}
        </span>
        <span>
          <Badge variant={finalizada ? 'success' : 'warning'}>
            {finalizada ? 'Finalizada' : 'En proceso'}
          </Badge>
        </span>
        <span className="flex justify-end">
          <ChevronRight
            className={cn('size-4 text-ink-400 transition-transform', abierta && 'rotate-90')}
            aria-hidden="true"
          />
        </span>
      </button>

      {abierta ? (
        <div className="grid gap-4 px-1 pb-4 lg:grid-cols-2">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Puntos calibrados
            </p>
            <ul className="mt-2 space-y-1.5">
              {calibracion.details.length === 0 ? (
                <li className="text-xs text-ink-500">Sin puntos registrados.</li>
              ) : (
                calibracion.details.map((punto) => (
                  <li
                    key={punto.calibration_detail_id}
                    className="flex items-center justify-between gap-3 rounded-control bg-ink-50/70 px-3 py-1.5"
                  >
                    <span className="truncate text-xs text-ink-700">
                      {punto.punto_servicio}
                      <span className="text-ink-400">
                        {' · '}
                        {punto.producto_nombre_producto ?? punto.producto_nombre ?? 'Sin producto'}
                      </span>
                    </span>
                    <span className="shrink-0 text-[11px] text-ink-400">
                      {formatDateTimeValue(punto.calibration_started_at)}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Volumen por manguera
            </p>
            <ul className="mt-2 space-y-1.5">
              {calibracion.pump_volumes.length === 0 ? (
                <li className="text-xs text-ink-500">
                  Las transacciones vinculadas a esta calibración todavía no se registran.
                </li>
              ) : (
                calibracion.pump_volumes.map((manguera) => (
                  <li
                    key={`${manguera.pump}-${manguera.nozzle}`}
                    className="flex items-center justify-between gap-3 rounded-control bg-ink-50/70 px-3 py-1.5"
                  >
                    <span className="text-xs text-ink-700">
                      Lado {manguera.pump} · Manguera {manguera.nozzle}
                      <span className="text-ink-400"> ({manguera.total_transactions} ops)</span>
                    </span>
                    <span className="shrink-0 font-mono text-xs text-ink-800 tabular-nums">
                      {formatNumber(manguera.total_volume)} gal ·{' '}
                      {formatCurrency(manguera.total_amount)}
                    </span>
                  </li>
                ))
              )}
            </ul>

            {calibracion.transactions.length > 0 ? (
              <p className="mt-3 text-[11px] text-ink-400">
                {calibracion.transactions.length} transacciones ·{' '}
                {formatNumber(toNumber(calibracion.volume_data.total_volume))} gal acumulados.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  );
}
